import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { getOnlineBookingDaycareAvailabilityForDatesThunk } from 'actions/onlineBooking/actions/OnlineBookingAvailabilityActions';
import { InlineDatePicker } from 'components/Form/InlineDatePicker/InlineDatePicker';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { useMarketplace } from 'hooks/useMarketplace';
import { useTimeZone } from 'hooks/useTimeZone';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { getOlbDateLimits } from 'reducers/masterData/MasterDataSelectors';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { JoinWaitlistCta } from 'components/OnlineBooking/JoinWaitlistCta/JoinWaitlistCta';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { Alert } from 'components/UI/Alert/Alert';
import {
    getOnlineBookingDaycareAvailabilityByDate,
    getOnlineBookingDaycareAvailabilityLoading,
    getOnlineBookingDaycareDates,
    getOnlineBookingDaycareItems
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { hasValidDaycareTimesForDate, MAX_DAYCARE_DAYS } from 'utils/daycareUtils';
import { getMarketplaceBusinessHoursForDate } from 'utils/marketplaceScheduleUtils';
import { resetDaycareAddonsOnDaysChange } from 'utils/onlineBookingAddons';

const dateKey = (date: Moment): string => date.format('YYYY-MM-DD');

export const OnlineBookingDaycareDatePicker: React.FC = () => {
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();

    const today = moment().tz(timeZone);

    const olbDateLimits = useSelector(getOlbDateLimits);
    const selectedDates = useSelector(getOnlineBookingDaycareDates);
    const daycareBookingItems = useSelector(getOnlineBookingDaycareItems);
    const availabilityByDate = useSelector(getOnlineBookingDaycareAvailabilityByDate);
    const daycareAvailabilityLoading = useSelector(getOnlineBookingDaycareAvailabilityLoading);
    const sharedDaycareService = React.useMemo(() => {
        return daycareBookingItems.find(item => item.service)?.service;
    }, [daycareBookingItems]);

    const limitPreferences = marketplace.onlineBookingPreferences.bookingDateLimits;

    const minDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.from?.find(limit => limit.id === limitPreferences.from)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.from]
    );

    const maxDate = React.useMemo(
        () =>
            today
                .clone()
                .add(
                    olbDateLimits.to?.find(limit => limit.id === limitPreferences.to)
                        ?.offsetSeconds ?? 0,
                    'seconds'
                ),
        [today, olbDateLimits, limitPreferences.to]
    );

    // Once the pet parent leaves the step, by any way out, days that changed since the Add-Ons
    // were set restore them to the default. The clicks while picking days never count.
    const latestDaycareBookingItems = React.useRef(daycareBookingItems);
    latestDaycareBookingItems.current = daycareBookingItems;

    React.useEffect(
        () => () => {
            const items = latestDaycareBookingItems.current;
            const updatedItems = items.map(resetDaycareAddonsOnDaysChange);

            if (updatedItems.some((item, index) => item !== items[index])) {
                dispatch(updateOnlineBookingBookingItems(updatedItems));
            }
        },
        []
    );

    const backHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.ServiceList));
    };

    const datesChangeHandler = React.useCallback(
        async (dates: Array<Moment>) => {
            const updatedItems = daycareBookingItems.map(item => ({
                ...item,
                days: dates.map(date => ({ date: date.clone() })),
                dropOffTime: undefined,
                pickUpTime: undefined
            }));

            dispatch(updateOnlineBookingBookingItems(updatedItems));

            if (!sharedDaycareService) {
                return;
            }

            const addedDates = dates.filter(
                date => !selectedDates.some(selected => selected.isSame(date, 'day'))
            );

            if (addedDates.length === 0) {
                return;
            }

            await dispatch(getOnlineBookingDaycareAvailabilityForDatesThunk(addedDates));
        },
        [daycareBookingItems, selectedDates, sharedDaycareService]
    );

    const isDateDisabled = React.useCallback(
        (calendarDate: Date) => {
            if (!sharedDaycareService) {
                return true;
            }

            return !hasValidDaycareTimesForDate(
                marketplace,
                sharedDaycareService,
                moment(calendarDate).tz(timeZone, true)
            );
        },
        [marketplace, sharedDaycareService, timeZone]
    );

    const getDisabledDateTooltip = React.useCallback(
        (calendarDate: Date) => {
            const date = moment(calendarDate).tz(timeZone, true);

            if (getMarketplaceBusinessHoursForDate(marketplace, date) !== null) {
                return undefined;
            }

            return `Marketplace closed on ${date.format('MMM D, YYYY')}`;
        },
        [marketplace, timeZone]
    );

    const submitHandler = () => {
        dispatch(navigateToStep(OnlineBookingStep.TimePicker));
    };

    const unavailableSelectedDates = React.useMemo(() => {
        return selectedDates.filter(date => {
            const availability = availabilityByDate[dateKey(date)];

            return !!availability && availability.dropOffSlots.length === 0;
        });
    }, [availabilityByDate, selectedDates]);

    const allSelectedDatesHaveAvailability = React.useMemo(() => {
        return (
            selectedDates.length > 0 &&
            selectedDates.every(date => {
                const availability = availabilityByDate[dateKey(date)];

                return !!availability && availability.dropOffSlots.length > 0;
            })
        );
    }, [availabilityByDate, selectedDates]);

    const canContinue =
        !!sharedDaycareService &&
        selectedDates.length > 0 &&
        !daycareAvailabilityLoading &&
        allSelectedDatesHaveAvailability;

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn>
                <h1 className="h2 underlined w-full mb-5">Select days</h1>

                <InlineDatePicker
                    multipleMode
                    max={MAX_DAYCARE_DAYS}
                    minDate={minDate}
                    maxDate={maxDate}
                    timeZone={timeZone}
                    value={selectedDates}
                    isDateDisabled={isDateDisabled}
                    getDisabledDateTooltip={getDisabledDateTooltip}
                    onChange={datesChangeHandler}
                />

                {unavailableSelectedDates.length > 0 && (
                    <Alert
                        variant="warning"
                        icon={faInfoCircle}
                        className="mt-4"
                        text={`No availability on ${unavailableSelectedDates
                            .map(date => date.format('MMM D'))
                            .join(', ')}. Remove ${
                            unavailableSelectedDates.length > 1 ? 'those days' : 'that day'
                        } to continue.`}
                    />
                )}

                <JoinWaitlistCta category={MarketplaceServiceCategory.DAYCARE} />
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        label="Continue"
                        loading={daycareAvailabilityLoading}
                        disabled={!canContinue}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
